import fp from "lodash/fp"
import { createSelector } from "reselect"

import Passenger from "../../../../../app/scripts/containers/mutable/Passenger"
import { getBookingToken, getOriginalPassengers } from "../../states/reducer"
import { getInfantsFareSupported } from "../../utils/reducer"
import { passengersValuesSelector } from "./passengersSelectors"
import { getAffilId } from "common/services/options/selectors"
import { getCombinations, getDefinitions } from "../../baggage/reducer"
import { getInsurancePassengers } from "booking/services/additionalServices/services/insurance/reducer"
import { INSURANCE_NAMES } from "common/consts/insurance"

export const passengerBaggageCountersSelector = createSelector(
  passengersValuesSelector,
  getDefinitions,
  getCombinations,
  (passengersValues, definitions, combinations) => index => {
    const {
      baggage: { handBag, holdBag },
    } = passengersValues[index]

    const handBagCombination = combinations.handBag[handBag].indices
    const cabinBagCombination = handBagCombination.filter(
      bagIndex => definitions.handBag[bagIndex].category === "cabinBag",
    )

    const freePersonalItemCombination = handBagCombination.filter(
      bagIndex =>
        definitions.handBag[bagIndex].category === "personalItem" &&
        definitions.handBag[bagIndex].price.amount === 0,
    )
    const paidCabinBagCombination = handBagCombination.filter(
      bagIndex =>
        definitions.handBag[bagIndex].category === "cabinBag" &&
        definitions.handBag[bagIndex].price.amount > 0,
    )

    const holdBagCombination = combinations.holdBag[holdBag].indices

    const holdBags = holdBagCombination.length
    const freeHoldBags = holdBagCombination.filter(
      comb => definitions.holdBag[comb].price.amount === 0,
    ).length
    const pureHandBags = cabinBagCombination.length
    const paidHandBags = paidCabinBagCombination.length
    const freePersonalItems = freePersonalItemCombination.length

    return {
      holdBags,
      freeHoldBags,
      paidHandBags,
      handBags: pureHandBags,
      extraFreeHandBags: freePersonalItems,
    }
  },
)

export const passengersCategoryCounterSelector = createSelector(
  passengersValuesSelector,
  passengersValues => {
    let { adult, teen, child, infant } = fp.countBy(x => x.category, passengersValues)

    // FIXME: convert function
    adult = fp.isNumber(adult) ? adult : 0
    teen = fp.isNumber(teen) ? teen : 0
    child = fp.isNumber(child) ? child : 0
    infant = fp.isNumber(infant) ? infant : 0

    // Keep 0 fallback as consts may be undefined in initial call
    // BEWARE: Do not use child category yet
    return {
      total: adult + teen + child + infant,
      // TODO: check if this is ok
      // adult: adult + child,
      // child: 0,
      adult,
      child,
      teen,
      infant,
    }
  },
)

// TODO: maybe refactor to make it more efficient
export const passengersBagsCountersSelector = createSelector(
  passengerBaggageCountersSelector,
  passengersCategoryCounterSelector,
  (passengerBagsCounters, passengersCategoryCounter) => {
    const passengersBagsCounters = fp
      .range(0, passengersCategoryCounter.total)
      .map(passengerBagsCounters)
    return {
      handBags: fp.sumBy("handBags", passengersBagsCounters), // free / paid
      holdBags: fp.sumBy("holdBags", passengersBagsCounters),
      extraFreeHandBags: fp.sumBy("extraFreeHandBags", passengersBagsCounters),
      freeHoldBags: fp.sumBy("freeHoldBags", passengersBagsCounters),
    }
  },
)

export const passengerCountersSelector = createSelector(
  passengersCategoryCounterSelector,
  passengersBagsCountersSelector,
  fp.merge,
)

export const separatePassengersCategoriesPricesSelector = createSelector(
  getInfantsFareSupported,
  passengerCountersSelector,
  (infantsFareSupported, passengerCounters) =>
    (infantsFareSupported && passengerCounters.infant > 0) || passengerCounters.child > 0,
)

export const passengerCanBeRemovedSelector = createSelector(
  passengerCountersSelector,
  passengerCounters => passengerCounters.total > 1,
)

export const hasAdultSelector = createSelector(
  passengerCountersSelector,
  passengerCounters => passengerCounters.adult > 0,
)

export const isEnoughAdults = createSelector(
  passengerCountersSelector,
  passengerCounters => passengerCounters.adult >= passengerCounters.infant,
)

export const standardPassengersSelector = createSelector(
  passengersValuesSelector,
  passengerBaggageCountersSelector,
  (passengersValues, passengerBagsCounters) =>
    // TODO: add to standard passenger
    //  bags: number,
    //  handBags: number,
    //  extraHandBags: number,

    passengersValues.map((p, i) => {
      const passengersBagsCounters = passengerBagsCounters(i)

      return new Passenger({
        ...p, // pass same format values from passenger
        // these values may not be required or initialized yet
        document: p.idNumber
          ? {
              code: p.idNumber,
              expiration:
                p.idExpirationYear && !p.idNoExpiration
                  ? new Date(p.idExpirationYear, p.idExpirationMonth - 1, p.idExpirationDay)
                  : undefined,
            }
          : undefined,
        firstname: p.firstname
          ? p.firstname.replace("|", " ") // Hack for middle name
          : "",
        birth: p.birthYear ? new Date(p.birthYear, p.birthMonth - 1, p.birthDay) : "",
        bags: passengersBagsCounters.holdBags,
        handBags: passengersBagsCounters.handBags,
        extraHandBags: passengersBagsCounters.extraFreeHandBags,
        freeHoldBags: passengersBagsCounters.freeHoldBags,
      })
    }),
)

export const checkFlightsParamsSelector = createSelector(
  getAffilId,
  getBookingToken,
  getOriginalPassengers,
  passengerCountersSelector,
  (affilId, bookingToken, originalPassengers, passengerCounters) => firstRequest => {
    const useOriginals = firstRequest || passengerCounters.total === 0

    // TODO: remove once other fares ready
    const adults = useOriginals
      ? originalPassengers.adult + originalPassengers.teen
      : passengerCounters.adult + passengerCounters.teen

    const commonParams = {
      affily: affilId,
      booking_token: bookingToken,

      // During redirect to TY page can happen passengerCounters.adult === 0
      // Need to set at least adults === 1 to keep checkFlights correct
      // Load original passengers in first request
      adults,
      // teens: useOriginals ? originalPassengers.teen : passengerCounters.teen,
      teens: 0,
      children: useOriginals ? originalPassengers.child : passengerCounters.child,
      infants: useOriginals ? originalPassengers.infant : passengerCounters.infant,
    }

    return firstRequest ? { ...commonParams, first_request: true } : commonParams
  },
)

export const insuranceCountSelector = createSelector(
  getInsurancePassengers,
  passengers => Object.values(passengers).filter(x => x !== INSURANCE_NAMES.NONE).length,
)

export default createSelector(
  [insuranceCountSelector, passengerCountersSelector, separatePassengersCategoriesPricesSelector],
  (insuranceCount, passengerCounters, separatePassengersCategoriesPrices) => ({
    insuranceCount,
    passengerCounters,
    separatePassengersCategoriesPrices,
  }),
)
